// ============================================
// Omflix Frontend API Client
// ============================================
import type {
  UserProfile,
  CreateProfileDto,
  UpdateProfileDto,
  WatchStats,
  SceneSearchResult,
  WebhookSettings,
  UserSessionInfo,
} from '@omflix/shared';

export type {
  UserProfile,
  CreateProfileDto,
  UpdateProfileDto,
  WatchStats,
  SceneSearchResult,
  WebhookSettings,
  UserSessionInfo,
};

export interface User {
  id: string;
  username: string;
  displayName: string | null;
  role: 'admin' | 'user';
  preferredLanguage: string;
}

export interface MediaItem {
  id: string;
  type: 'movie' | 'show';
  tmdbId?: number;
  imdbId?: string;
  title: string;
  titleHe?: string;
  originalTitle?: string;
  overview?: string;
  overviewHe?: string;
  tagline?: string;
  year?: number;
  runtime?: number;
  rating?: number;
  voteCount?: number;
  posterPath?: string;
  backdropPath?: string;
  logoPath?: string;
  genres: string[];
  trailerKey?: string;
  inLibrary: boolean;
  inWatchlist?: boolean;
  cast?: Array<{ name: string; character: string; profilePath: string | null }>;
  crew?: Array<{ name: string; job: string; profilePath: string | null }>;
  files?: MediaFile[];
  seasons?: Season[];
  userState?: {
    progress?: WatchProgress | null;
    inWatchlist: boolean;
    isFavorite: boolean;
  };
}

export interface Season {
  id: string;
  showId: string;
  seasonNumber: number;
  name: string;
  episodes: Episode[];
}

export interface Episode {
  id: string;
  showId: string;
  seasonId: string;
  seasonNumber: number;
  episodeNumber: number;
  title: string;
  overview?: string;
  stillPath?: string;
  runtime?: number;
  mediaFile?: MediaFile | null;
}

export interface MediaFile {
  id: string;
  mediaItemId: string;
  episodeId?: string;
  fileName: string;
  fileSize?: string;
  container?: string;
  duration?: number;
  videoCodec?: string;
  videoProfile?: string;
  width?: number;
  height?: number;
  framerate?: number;
  isHdr?: boolean;
  hdrFormat?: string;
  audioStreams?: Array<{
    index: number;
    codec: string;
    channels: number;
    language: string | null;
    title: string | null;
    isDefault: boolean;
  }>;
  subtitleStreams?: Array<{
    index: number;
    codec: string;
    language: string | null;
    title: string | null;
    isForced: boolean;
    isDefault: boolean;
  }>;
}

export interface WatchProgress {
  id: string;
  mediaItemId: string;
  episodeId?: string;
  currentTime: number;
  duration: number;
  percentage: number;
  finished: boolean;
}

export interface WatchHistoryItem {
  id: string;
  mediaItemId: string;
  title: string;
  titleHe?: string | null;
  originalTitle?: string | null;
  type: 'movie' | 'show';
  posterPath: string | null;
  backdropPath: string | null;
  year: number | null;
  currentTime: number;
  duration: number;
  percentage: number;
  finished: boolean;
  updatedAt: string;
  stoppedMinute: number;
  totalMinutes: number;
  episode?: {
    id: string;
    seasonNumber: number;
    episodeNumber: number;
    title: string;
    stillPath: string | null;
  } | null;
}

export interface HomeFeed {
  hero: MediaItem | null;
  heroCarousel?: MediaItem[];
  continueWatching: any[];
  inLibrary?: MediaItem[];
  recentlyAddedMovies: MediaItem[];
  recentlyAddedShows: MediaItem[];
  trendingMovies?: MediaItem[];
  trendingShows?: MediaItem[];
  topRated?: MediaItem[];
  actionMovies?: MediaItem[];
  scifiMovies?: MediaItem[];
  comedyMovies?: MediaItem[];
  animationMovies?: MediaItem[];
  thrillerMovies?: MediaItem[];
  popularShows?: MediaItem[];
  watchlist: MediaItem[];
}

export interface MoodFeed {
  vibe: string;
  emoji: string;
  title: string;
  titleHe: string;
  description: string;
  descriptionHe: string;
  items: MediaItem[];
}

export interface BecauseYouWatchedRow {
  sourceId: string;
  sourceTitle: string;
  sourceTitleHe?: string | null;
  reason: string;
  reasonHe: string;
  items: MediaItem[];
}

export interface SmartCollection {
  id: string;
  tmdbId?: number;
  name: string;
  overview?: string;
  posterPath: string | null;
  backdropPath: string | null;
  isCustom: boolean;
  itemsCount: number;
  items: MediaItem[];
}

export interface SearchResult {
  inLibrary: MediaItem[];
  discover: MediaItem[];
}

export interface TrendingCommunityItem extends MediaItem {
  communityRank: number;
  communityScore: number;
  watchersCount: number;
  completionRate: number;
  trendingBadge: string;
}

export interface PopularCategoryItem {
  id: string;
  nameHe: string;
  nameEn: string;
  emoji: string;
  query: string;
  genreId?: number;
}

export interface SearchDiscoveryData {
  trendingSearches: string[];
  popularCategories: PopularCategoryItem[];
  trendingCommunity: TrendingCommunityItem[];
  inLibraryTrending: MediaItem[];
}

export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem('omflix_device_id');
  const UUID_REGEX = /^[0-9a-fA-F-]{36}$/;
  if (!id || !UUID_REGEX.test(id)) {
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
    localStorage.setItem('omflix_device_id', id);
  }
  return id;
}

export function getBaseApiUrl(): string {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem('omflix_server_url');
  if (stored) {
    return stored.replace(/\/+$/, '');
  }

  // Detect Capacitor or native mobile wrapper
  const isCapacitor =
    (window as any).Capacitor !== undefined ||
    window.location.protocol === 'capacitor:' ||
    (!window.location.origin.includes(':5173') &&
      !window.location.origin.includes(':8096') &&
      (window.location.protocol.startsWith('file:') ||
        (window.location.hostname === 'localhost' && !window.location.port)));

  if (isCapacitor) {
    // Default to Tailscale VPN IP of the home server (accessible everywhere)
    return 'http://100.127.161.16:8096';
  }
  return stored ? stored.replace(/\/+$/, '') : '';
}

/** Appends the session token for media elements that cannot send Authorization. */
export function withAccessToken(url: string): string {
  if (!url || typeof window === 'undefined') return url;
  const token = localStorage.getItem('omflix_token');
  if (!token || url.includes('access_token=')) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}access_token=${encodeURIComponent(token)}`;
}

export function apiUrl(endpoint: string): string {
  if (!endpoint) return '';
  if (
    endpoint.startsWith('http://') ||
    endpoint.startsWith('https://') ||
    endpoint.startsWith('blob:') ||
    endpoint.startsWith('data:')
  ) {
    return endpoint;
  }
  const base = getBaseApiUrl();
  return `${base}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
}

/**
 * Builds the fully-qualified download URL for a media file, honoring the
 * configured server base (Tailscale/LAN) instead of assuming same-origin.
 */
export function getStreamDownloadUrl(fileId: string): string {
  return apiUrl(`/api/v1/stream/${fileId}/download`);
}

/**
 * Fetches the raw movie file as a streaming Response, with the same auth
 * headers the rest of the client uses. Callers read `response.body` directly
 * (e.g. to pipe it into a File System Access API writable) rather than going
 * through `request()`, which assumes a JSON body.
 */
export async function fetchDownloadStream(fileId: string, signal?: AbortSignal): Promise<Response> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('omflix_token') : null;
  const deviceId = getDeviceId();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (deviceId) headers['X-Device-Id'] = deviceId;

  return fetch(getStreamDownloadUrl(fileId), {
    headers,
    credentials: 'include',
    signal,
  });
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('omflix_token') : null;
  const deviceId = getDeviceId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options?.headers as Record<string, string>),
  };
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (deviceId && !headers['X-Device-Id']) {
    headers['X-Device-Id'] = deviceId;
  }
  const activeProfile = typeof window !== 'undefined' ? localStorage.getItem('omflix_active_profile') : null;
  if (activeProfile && !headers['X-Profile-Id']) {
    try {
      const parsed = JSON.parse(activeProfile);
      if (parsed?.id) {
        headers['X-Profile-Id'] = parsed.id;
      }
    } catch {
      headers['X-Profile-Id'] = activeProfile;
    }
  }

  const fullUrl = apiUrl(url);
  try {
    const res = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!res.ok) {
      let errorMsg = `HTTP Error ${res.status}`;
      try {
        const errJson = await res.json();
        errorMsg = errJson.message || errJson.error || errorMsg;
      } catch {
        // Ignore
      }
      throw new Error(errorMsg);
    }

    const json = await res.json();
    return json.data !== undefined ? json.data : json;
  } catch (err: any) {
    // If running in Capacitor/native and endpoint is relative, try automatic dual-channel fallback
    const isCapacitor =
      typeof window !== 'undefined' &&
      ((window as any).Capacitor !== undefined ||
        window.location.protocol === 'capacitor:' ||
        window.location.protocol.startsWith('file:') ||
        (window.location.hostname === 'localhost' && !window.location.port));

    if (isCapacitor && !url.startsWith('http')) {
      const currentBase = getBaseApiUrl();
      const fallbackBase = currentBase.includes('100.127.161.16')
        ? 'http://192.168.1.213:8096'
        : 'http://100.127.161.16:8096';

      try {
        const fallbackUrl = `${fallbackBase}${url.startsWith('/') ? '' : '/'}${url}`;
        const fallbackRes = await fetch(fallbackUrl, {
          ...options,
          headers,
          credentials: 'include',
        });
        if (fallbackRes.ok) {
          localStorage.setItem('omflix_server_url', fallbackBase);
          const json = await fallbackRes.json();
          return json.data !== undefined ? json.data : json;
        }
      } catch {
        // Fallback failed as well, rethrow original error
      }
    }
    throw err;
  }
}

function getAdminUrl(endpoint: string): string {
  return endpoint;
}

export const api = {
  // Auth
  async getMe(): Promise<{ anonymous: boolean; user: User | null }> {
    return request('/api/v1/auth/me');
  },
  async login(username: string, password: string): Promise<{ user: User; token: string }> {
    const res = await request<{ user: User; token: string }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    if (res?.token && typeof window !== 'undefined') {
      localStorage.setItem('omflix_token', res.token);
    }
    return res;
  },
  async register(
    username: string,
    password: string,
    displayName?: string,
  ): Promise<{ user: User; token: string }> {
    const res = await request<{ user: User; token: string }>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password, displayName }),
    });
    if (res?.token && typeof window !== 'undefined') {
      localStorage.setItem('omflix_token', res.token);
    }
    return res;
  },
  async logout(): Promise<void> {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('omflix_token');
    }
    return request('/api/v1/auth/logout', { method: 'POST' });
  },

  // Media
  async getHomeFeed(): Promise<HomeFeed> {
    return request('/api/v1/media/home');
  },
  async getMedia(id: string): Promise<MediaItem> {
    return request(`/api/v1/media/${id}`);
  },
  async getTrailer(
    id: string,
    params?: { title?: string; year?: number; tmdbId?: number },
  ): Promise<{ trailerKey: string | null }> {
    const qs = new URLSearchParams();
    if (params?.title) qs.set('title', params.title);
    if (params?.year) qs.set('year', String(params.year));
    if (params?.tmdbId) qs.set('tmdbId', String(params.tmdbId));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/api/v1/media/${id}/trailer${query}`);
  },
  async listMedia(params?: Record<string, string>): Promise<{ items: MediaItem[]; count: number }> {
    const query = new URLSearchParams(params).toString();
    return request(`/api/v1/media${query ? `?${query}` : ''}`);
  },
  async search(query: string): Promise<SearchResult> {
    return request(`/api/v1/media/search?q=${encodeURIComponent(query)}`);
  },
  async getSearchDiscovery(): Promise<SearchDiscoveryData> {
    return request('/api/v1/media/search/discovery');
  },
  async logSearchQuery(query: string, selectedMediaId?: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>('/api/v1/media/search/log', {
      method: 'POST',
      body: JSON.stringify({ query, selectedMediaId }),
    }).catch(() => ({ success: false }));
  },

  // Progress & Watchlist
  async updateProgress(data: {
    mediaItemId: string;
    episodeId?: string;
    currentTime: number;
    duration: number;
  }): Promise<void> {
    return request('/api/v1/media/progress', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async getWatchHistory(): Promise<WatchHistoryItem[]> {
    return request('/api/v1/media/history');
  },
  async toggleWatchlist(mediaItemId: string): Promise<{ inWatchlist: boolean }> {
    return request('/api/v1/media/watchlist', {
      method: 'POST',
      body: JSON.stringify({ mediaItemId }),
    });
  },
  async toggleFavorite(mediaItemId: string): Promise<{ isFavorite: boolean }> {
    return request('/api/v1/media/favorites', {
      method: 'POST',
      body: JSON.stringify({ mediaItemId }),
    });
  },

  // Scanner
  async triggerScan(): Promise<{ message: string }> {
    return request('/api/v1/scanner/scan', { method: 'POST' });
  },
  async getScannerStatus(): Promise<any> {
    return request('/api/v1/scanner/status');
  },

  // Downloads
  async searchDownloads(params: {
    title: string;
    tmdbId?: number;
    imdbId?: string;
    year?: number;
  }): Promise<DownloadRelease[]> {
    const q = new URLSearchParams();
    q.set('title', params.title);
    if (params.tmdbId) q.set('tmdbId', params.tmdbId.toString());
    if (params.imdbId) q.set('imdbId', params.imdbId);
    if (params.year) q.set('year', params.year.toString());
    const res = await request<any>(`/api/v1/downloads/search?${q.toString()}`);
    return Array.isArray(res) ? res : (res?.data || []);
  },
  async startDownload(data: {
    tmdbId?: number;
    imdbId?: string;
    title: string;
    year?: number;
    quality?: string;
    magnetUrl: string;
    infoHash?: string;
  }): Promise<DownloadJob> {
    const res = await request<any>('/api/v1/downloads', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res?.data !== undefined ? res.data : res;
  },
  async getDownloads(): Promise<DownloadJob[]> {
    const res = await request<any>('/api/v1/downloads');
    return Array.isArray(res) ? res : (res?.data || []);
  },
  async cancelDownload(id: string): Promise<void> {
    return request(`/api/v1/downloads/${id}`, { method: 'DELETE' });
  },
  async getDownloadSettings(): Promise<{ hasRealDebrid: boolean; maskedKey: string | null }> {
    const res = await request<any>('/api/v1/downloads/settings');
    return res?.data !== undefined ? res.data : (res || { hasRealDebrid: false, maskedKey: null });
  },
  async updateDownloadSettings(data: { realDebridApiKey?: string }): Promise<void> {
    return request('/api/v1/downloads/settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  getStreamDownloadUrl,
  fetchDownloadStream,

  // Admin (localhost only)
  async getAdminHealth(): Promise<any> {
    return request(getAdminUrl('/api/v1/admin/health'));
  },
  async getAdminSummary(): Promise<any> {
    return request(getAdminUrl('/api/v1/admin/library/summary'));
  },
  async getAdminStreams(): Promise<any[]> {
    return request(getAdminUrl('/api/v1/admin/streams'));
  },
  async getAdminSettings(): Promise<Record<string, string>> {
    return request(getAdminUrl('/api/v1/admin/settings'));
  },
  async updateAdminSettings(settings: Record<string, string>): Promise<void> {
    return request(getAdminUrl('/api/v1/admin/settings'), {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },
  async clearCache(): Promise<void> {
    return request(getAdminUrl('/api/v1/admin/maintenance/clear-cache'), { method: 'POST' });
  },
  async fixMatch(data: {
    mediaItemId: string;
    tmdbId: number;
    type: 'movie' | 'show';
    customAlias?: string;
  }): Promise<void> {
    return request(getAdminUrl('/api/v1/admin/library/fix-match'), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async getAdminUsers(): Promise<Array<{
    id: string;
    username: string;
    displayName: string;
    role: 'admin' | 'user';
    isActive: boolean;
    createdAt: string;
  }>> {
    return request(getAdminUrl('/api/v1/admin/users'));
  },
  async createAdminUser(data: {
    username: string;
    password: string;
    displayName?: string;
    role?: 'admin' | 'user';
  }): Promise<any> {
    return request(getAdminUrl('/api/v1/admin/users'), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async toggleAdminUserActive(userId: string): Promise<any> {
    return request(getAdminUrl(`/api/v1/admin/users/${userId}/toggle`), {
      method: 'PUT',
    });
  },
  async updateAdminUserRole(userId: string, role: 'admin' | 'user'): Promise<any> {
    return request(getAdminUrl(`/api/v1/admin/users/${userId}/role`), {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },
  async resetAdminUserPassword(userId: string, password: string): Promise<any> {
    return request(getAdminUrl(`/api/v1/admin/users/${userId}/password`), {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  },
  async deleteAdminUser(userId: string): Promise<any> {
    return request(getAdminUrl(`/api/v1/admin/users/${userId}`), {
      method: 'DELETE',
    });
  },

  // Webhooks & Sessions
  async getAdminWebhooks(): Promise<WebhookSettings> {
    const res = await request<any>(getAdminUrl('/api/v1/admin/webhooks'));
    return res?.data || res;
  },
  async updateAdminWebhooks(data: Partial<WebhookSettings>): Promise<any> {
    return request(getAdminUrl('/api/v1/admin/webhooks'), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async testAdminWebhook(type: 'discord' | 'telegram'): Promise<{ success: boolean; message?: string }> {
    return request(getAdminUrl('/api/v1/admin/webhooks/test'), {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  },
  async getAdminSessions(): Promise<UserSessionInfo[]> {
    const res = await request<any>(getAdminUrl('/api/v1/admin/sessions'));
    return Array.isArray(res) ? res : (res?.data || []);
  },
  async revokeAdminSession(sessionId: string): Promise<any> {
    return request(getAdminUrl(`/api/v1/admin/sessions/${sessionId}`), {
      method: 'DELETE',
    });
  },

  // Profiles
  async getProfiles(): Promise<UserProfile[]> {
    const res = await request<any>('/api/v1/profiles');
    return Array.isArray(res) ? res : (res?.data || []);
  },
  async createProfile(data: CreateProfileDto): Promise<UserProfile> {
    const res = await request<any>('/api/v1/profiles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res?.data || res;
  },
  async updateProfile(id: string, data: UpdateProfileDto): Promise<UserProfile> {
    const res = await request<any>(`/api/v1/profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res?.data || res;
  },
  async deleteProfile(id: string): Promise<{ success: boolean }> {
    return request(`/api/v1/profiles/${id}`, { method: 'DELETE' });
  },
  async verifyProfilePin(id: string, pin: string): Promise<{ valid: boolean }> {
    return request(`/api/v1/profiles/${id}/verify-pin`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  },

  // Smart Discovery & Stats
  async getRandomMedia(params?: { type?: 'movie' | 'show'; genre?: string; minRating?: number }): Promise<MediaItem | null> {
    const q = new URLSearchParams();
    if (params?.type) q.set('type', params.type);
    if (params?.genre) q.set('genre', params.genre);
    if (params?.minRating) q.set('minRating', params.minRating.toString());
    const query = q.toString() ? `?${q.toString()}` : '';
    const res = await request<any>(`/api/v1/media/random${query}`);
    return res?.data !== undefined ? res.data : res;
  },
  async getSimilarMedia(id: string): Promise<MediaItem[]> {
    const res = await request<any>(`/api/v1/media/${id}/similar`);
    return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
  },
  async getPersonalStats(): Promise<WatchStats> {
    const res = await request<any>('/api/v1/media/stats');
    return res?.data !== undefined ? res.data : res;
  },
  async getRecommendations(): Promise<{ reason: string; reasonHe: string; items: MediaItem[] }> {
    const res = await request<any>('/api/v1/media/recommendations');
    return res?.data !== undefined ? res.data : (res || { reason: '', reasonHe: '', items: [] });
  },
  async searchScenes(query: string): Promise<SceneSearchResult[]> {
    const res = await request<any>(`/api/v1/media/scenes?q=${encodeURIComponent(query)}`);
    return Array.isArray(res) ? res : (res?.data || []);
  },
  async getSystemHealth(): Promise<any> {
    const res = await request<any>('/api/v1/health');
    return res?.data !== undefined ? res.data : res;
  },
  async getMood(vibe: string): Promise<MoodFeed> {
    const res = await request<any>(`/api/v1/media/mood?vibe=${encodeURIComponent(vibe)}`);
    return res?.data !== undefined ? res.data : res;
  },
  async getBecauseYouWatched(): Promise<BecauseYouWatchedRow[]> {
    const res = await request<any>('/api/v1/media/because-you-watched');
    return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
  },
  async getCollections(): Promise<SmartCollection[]> {
    const res = await request<any>('/api/v1/collections');
    return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
  },
  async searchOnlineSubtitles(params: { query?: string; imdbId?: string; tmdbId?: number; lang?: string }): Promise<any[]> {
    const q = new URLSearchParams();
    if (params.query) q.set('query', params.query);
    if (params.imdbId) q.set('imdbId', params.imdbId);
    if (params.tmdbId) q.set('tmdbId', params.tmdbId.toString());
    if (params.lang) q.set('lang', params.lang);
    const res = await request<any>(`/api/v1/subtitles/search?${q.toString()}`);
    return Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
  },
  async downloadSubtitle(subId: string, url?: string, language?: string): Promise<{ cacheId: string; url: string }> {
    const res = await request<any>('/api/v1/subtitles/download', {
      method: 'POST',
      body: JSON.stringify({ subId, url, language }),
    });
    return res?.data !== undefined ? res.data : res;
  },
  async createWatchParty(data: { hostId: string; hostName: string; mediaItemId: string; title: string; avatar?: string; profileId?: string }): Promise<any> {
    const res = await request<any>('/api/v1/watchparty/rooms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res?.data !== undefined ? res.data : res;
  },
  async joinWatchParty(code: string, data: { userId: string; name: string; avatar?: string; profileId?: string }): Promise<any> {
    const res = await request<any>(`/api/v1/watchparty/rooms/${code}/join`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res?.data !== undefined ? res.data : res;
  },
  async getWatchPartyState(code: string): Promise<any> {
    const res = await request<any>(`/api/v1/watchparty/rooms/${code}`);
    return res?.data !== undefined ? res.data : res;
  },
  async syncWatchParty(code: string, data: { userId: string; action: 'play' | 'pause' | 'seek' | 'heartbeat'; currentTime: number }): Promise<any> {
    const res = await request<any>(`/api/v1/watchparty/rooms/${code}/sync`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res?.data !== undefined ? res.data : res;
  },
  async sendWatchPartyMessage(code: string, data: { userId: string; userName: string; text?: string; reaction?: string; avatar?: string }): Promise<any> {
    const res = await request<any>(`/api/v1/watchparty/rooms/${code}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res?.data !== undefined ? res.data : res;
  },

  // Playback & Diagnostic Logs
  async getPlaybackLogs(query?: { limit?: number; category?: string; level?: string; search?: string }): Promise<any[]> {
    const q = new URLSearchParams();
    if (query?.limit) q.set('limit', query.limit.toString());
    if (query?.category) q.set('category', query.category);
    if (query?.level) q.set('level', query.level);
    if (query?.search) q.set('search', query.search);
    const qs = q.toString() ? `?${q.toString()}` : '';
    const res = await request<any>(getAdminUrl(`/api/v1/logs/playback${qs}`));
    return Array.isArray(res) ? res : (res?.data || []);
  },
  async getPlaybackSummary(): Promise<any> {
    const res = await request<any>(getAdminUrl('/api/v1/logs/playback/summary'));
    return res?.data !== undefined ? res.data : res;
  },
  async clearPlaybackLogs(): Promise<void> {
    return request(getAdminUrl('/api/v1/logs/playback'), { method: 'DELETE' });
  },
};

export interface DownloadRelease {
  id: string;
  title: string;
  quality: string;
  size: string;
  seeds: number;
  source: string;
  magnetUrl: string;
  infoHash: string;
}

export interface DownloadJob {
  id: string;
  tmdbId?: number;
  imdbId?: string;
  title: string;
  year?: number;
  quality?: string;
  status: 'queued' | 'downloading' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  downloadSpeed: string;
  downloadedSize: string;
  totalSize: string;
  eta: string;
  peers: number;
  errorMessage?: string;
  destinationPath: string;
  createdAt: string;
  completedAt?: string;
}
